import { Check, Video, X } from "lucide-react";

import { DoctorStatusBadge } from "@/components/common/status-badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatCurrency, formatDate, initials } from "@/lib/format";
import type { Doctor } from "@/types/doctor";

const NOT_PROVIDED = "Not provided";

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  const provided = Boolean(value && String(value).trim());
  return (
    <div>
      <dt className="text-[0.7rem] font-semibold tracking-wider text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className={provided ? "mt-1 text-sm break-words" : "mt-1 text-sm text-muted-foreground"}>
        {provided ? value : NOT_PROVIDED}
      </dd>
    </div>
  );
}

function Chips({ label, values }: { label: string; values: string[] }) {
  return (
    <div>
      <dt className="text-[0.7rem] font-semibold tracking-wider text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="mt-2 flex flex-wrap gap-1.5">
        {values.length === 0 ? (
          <span className="text-sm text-muted-foreground">{NOT_PROVIDED}</span>
        ) : (
          values.map((value) => (
            <span
              key={value}
              className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium"
            >
              {value}
            </span>
          ))
        )}
      </dd>
    </div>
  );
}

export function DoctorDetailsDrawer({
  doctor,
  open,
  onOpenChange,
  onApprove,
  onReject,
}: {
  doctor: Doctor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: (doctor: Doctor) => void;
  onReject: (doctor: Doctor) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-xl"
        aria-describedby={undefined}
      >
        {doctor && (
          <>
            <SheetHeader className="border-b border-border p-6">
              <SheetTitle className="sr-only">{doctor.name}</SheetTitle>
              <div className="flex items-start gap-4">
                <Avatar className="size-16 border border-border">
                  {doctor.profilePicture && (
                    <AvatarImage src={doctor.profilePicture} alt={doctor.name} />
                  )}
                  <AvatarFallback className="bg-primary text-base font-semibold text-primary-foreground">
                    {initials(doctor.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 text-left">
                  <p className="font-display text-xl font-semibold text-balance">{doctor.name}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {doctor.specialization} · {doctor.experience}{" "}
                    {doctor.experience === 1 ? "year" : "years"}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <DoctorStatusBadge status={doctor.status} />
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      <Video className="size-3.5" aria-hidden="true" />
                      {doctor.videoConsultationAvailable ? "Video consultation" : "In-person only"}
                    </span>
                  </div>
                </div>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto p-6">
              <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Email" value={doctor.email} />
                <Field label="Phone" value={doctor.phone} />
                <Field label="Hospital" value={doctor.hospitalName} />
                <Field label="Location" value={doctor.location} />
                <Field label="Consultation fee" value={formatCurrency(doctor.fees)} />
                <Field label="Registered" value={formatDate(doctor.createdAt)} />
              </dl>

              <div className="mt-6 space-y-5 border-t border-border pt-6">
                <Field label="Bio" value={doctor.bio} />
                <Chips label="Qualifications" values={doctor.qualifications ?? []} />
                <Chips label="Languages" values={doctor.languages ?? []} />
              </div>

              <div className="mt-6 border-t border-border pt-6">
                <p className="text-[0.7rem] font-semibold tracking-wider text-muted-foreground uppercase">
                  Weekly schedule
                </p>
                {(doctor.schedule ?? []).length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">{NOT_PROVIDED}</p>
                ) : (
                  <ul className="mt-3 divide-y divide-border overflow-hidden rounded-xl border border-border">
                    {doctor.schedule.map((slot, index) => (
                      <li
                        key={`${slot.day}-${slot.startTime}-${index}`}
                        className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
                      >
                        <span className="font-medium">{slot.day}</span>
                        <span className="text-muted-foreground tabular-nums">
                          {slot.startTime} – {slot.endTime}
                        </span>
                        <span
                          className={
                            slot.isAvailable
                              ? "inline-flex items-center gap-1 text-xs font-semibold text-success"
                              : "inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"
                          }
                        >
                          {slot.isAvailable ? (
                            <Check className="size-3.5" aria-hidden="true" />
                          ) : (
                            <X className="size-3.5" aria-hidden="true" />
                          )}
                          {slot.isAvailable ? "Available" : "Unavailable"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="flex gap-2 border-t border-border p-4">
              <Button variant="outline" className="flex-1" onClick={() => onReject(doctor)}>
                Reject
              </Button>
              <Button className="flex-1" onClick={() => onApprove(doctor)}>
                Approve
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
